import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { sortable } from 'react-sortable';

export const categoryShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  key: PropTypes.string,
  addon_count: PropTypes.number,
  known_to_the_storefront: PropTypes.bool,
});

const GripIcon = () => (
  <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true" focusable="false">
    <circle cx="2" cy="3" r="1.4" />
    <circle cx="8" cy="3" r="1.4" />
    <circle cx="2" cy="8" r="1.4" />
    <circle cx="8" cy="8" r="1.4" />
    <circle cx="2" cy="13" r="1.4" />
    <circle cx="8" cy="13" r="1.4" />
  </svg>
);

// One section of the gifts page.
//
// Renaming and deleting both happen in place: a rename swaps the name for an
// input, a delete opens a panel under the row. Neither navigates, because both
// are decisions you make while looking at the rest of the list -- a delete in
// particular needs the other categories on screen to choose where its products
// go.
export const CategoryRow = (props) => {
  const {
    category, categories, first, last, error,
    onRename, onDelete, onMoveUp, onMoveDown,
    onDragStart, onDragEnd, onTouchStart,
    draggable, items, onSortItems, moveInMiddle, sortId,
    ...rowProps
  } = props;

  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [name, setName] = useState(category.name);
  const [moveTo, setMoveTo] = useState('');
  // Same as the add-ons list: draggable up front, and the handle is a span, not
  // a button -- Chrome refuses to start a drag from a form control, so a button
  // handle simply never drags. Handle-only is enforced in onDragStart.
  const fromHandle = useRef(false);
  const inputRef = useRef(null);

  useEffect(() => { setName(category.name); }, [category.name]);
  useEffect(() => { if (renaming && inputRef.current) inputRef.current.focus(); }, [renaming]);

  const others = categories.filter((option) => option.id !== category.id);
  const count = category.addon_count || 0;

  const saveRename = () => {
    onRename(category, name.trim(), () => setRenaming(false));
  };

  const onRenameKeyDown = (event) => {
    if (event.key === 'Enter') { event.preventDefault(); saveRename(); }
    if (event.key === 'Escape') { setName(category.name); setRenaming(false); }
  };

  return (
    <div className="ac-row-wrap">
      <div
        className="ac-grid ac-row"
        draggable
        onMouseDown={(event) => { fromHandle.current = Boolean(event.target.closest('.aa-handle')); }}
        onDragStart={(event) => {
          if (!fromHandle.current) { event.preventDefault(); return; }
          if (onDragStart) onDragStart(event);
        }}
        onDragEnd={(event) => { fromHandle.current = false; if (onDragEnd) onDragEnd(event); }}
        {...rowProps}
      >
        <div>
          <span
            className="aa-handle"
            role="button"
            tabIndex={-1}
            aria-label={`Reorder ${category.name}`}
            title="Drag to change the order customers see. The arrows do the same from the keyboard."
            data-id={sortId}
            onTouchStart={onTouchStart}
          >
            <GripIcon />
          </span>
        </div>

        <div className="ac-names">
          {renaming ? (
            <div className="ac-rename">
              {/* Distinct from the button's "Rename X" so a screen reader
                  announces the field and the control differently. */}
              <label className="aa-label" htmlFor={`ac-rename-${category.id}`}>
                {`New name for ${category.name}`}
              </label>
              <input
                id={`ac-rename-${category.id}`}
                ref={inputRef}
                className="aa-input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                onKeyDown={onRenameKeyDown}
              />
              <div className="ac-rename-actions">
                <button type="button" className="aa-btn aa-btn--primary" onClick={saveRename}>Save</button>
                <button
                  type="button"
                  className="aa-btn"
                  onClick={() => { setName(category.name); setRenaming(false); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <span className="ac-name">{category.name}</span>
              <a className="ac-count" href={`/admin/addons?category=${category.id}`}>
                {`${count} add-on${count === 1 ? '' : 's'}`}
              </a>
            </>
          )}
        </div>

        <div className="ac-arrows">
          <button
            type="button"
            className="ac-arrow"
            aria-label={`Move ${category.name} up`}
            disabled={first}
            onClick={() => onMoveUp(category)}
          >
            ↑
          </button>
          <button
            type="button"
            className="ac-arrow"
            aria-label={`Move ${category.name} down`}
            disabled={last}
            onClick={() => onMoveDown(category)}
          >
            ↓
          </button>
        </div>

        <div className="ac-actions">
          <button
            type="button"
            className="aa-btn"
            aria-label={`Rename ${category.name}`}
            onClick={() => { setRenaming(true); setDeleting(false); }}
          >
            Rename
          </button>
          <button
            type="button"
            className="aa-btn aa-btn--danger"
            aria-label={`Delete ${category.name}`}
            onClick={() => { setDeleting((open) => !open); setRenaming(false); }}
          >
            Delete
          </button>
        </div>
      </div>

      {error ? <p className="ac-error" role="alert">{error}</p> : null}

      {deleting ? (
        <div className="ac-delete">
          {count > 0 ? (
            <div className="ac-delete-copy">
              <label htmlFor={`ac-move-${category.id}`}>
                {`Move its ${count} add-on${count === 1 ? '' : 's'} to`}
              </label>
              <select
                id={`ac-move-${category.id}`}
                className="aa-select"
                value={moveTo}
                onChange={(event) => setMoveTo(event.target.value)}
              >
                <option value="">No category</option>
                {others.map((option) => (
                  <option key={option.id} value={option.id}>{option.name}</option>
                ))}
              </select>
              <span>{`then delete “${category.name}”.`}</span>
            </div>
          ) : (
            <p className="ac-delete-copy">{`“${category.name}” has no add-ons. Delete it?`}</p>
          )}

          {/* The five the storefront knows by name carry more than a heading. */}
          {category.known_to_the_storefront ? (
            <p className="ac-warn">
              The store knows this section by name: deleting it drops its intro line, its
              place in the tab strip and its ranking on the shelf. Its add-ons keep selling
              wherever you move them.
            </p>
          ) : null}

          <div className="ac-delete-actions">
            <button type="button" className="aa-btn" onClick={() => setDeleting(false)}>Cancel</button>
            <button
              type="button"
              className="aa-btn aa-btn--danger"
              onClick={() => onDelete(category, moveTo)}
            >
              Delete category
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

CategoryRow.propTypes = {
  category: categoryShape.isRequired,
  categories: PropTypes.arrayOf(categoryShape).isRequired,
  first: PropTypes.bool,
  last: PropTypes.bool,
  error: PropTypes.string,
  onRename: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func,
  onTouchStart: PropTypes.func,
  draggable: PropTypes.bool,
  items: PropTypes.arrayOf(categoryShape),
  onSortItems: PropTypes.func,
  moveInMiddle: PropTypes.bool,
  sortId: PropTypes.number,
};

CategoryRow.defaultProps = {
  first: false,
  last: false,
  error: null,
  onDragStart: undefined,
  onDragEnd: undefined,
  onTouchStart: undefined,
  draggable: undefined,
  items: undefined,
  onSortItems: undefined,
  moveInMiddle: undefined,
  sortId: undefined,
};

export default sortable(CategoryRow);
